import React from 'react';
import classNames from 'classnames';

import {
  Container,
  Image,
} from '@uncinc/uncinc-react-kitchen-sink';

import type { ImmutableEntity } from '@uncinc/react-drupal-core/types';
import type { AspectRatio } from '@uncinc/react-drupal-core/dist/Components/DynamicMediaItem';
import {
  MasonryGrid,
  Text,
  Slider,
} from '../../../components';
import { SCREEN_NORMAL } from '../../../config/constants';
import { ParagraphTextImageGrid as Type } from '../../../types/uncinc_profile/paragraphs';
import type { HeadingTag } from '../../../types';

import './style.scss';

type DisplayMode = 'default' | 'slider';

interface ParagraphTextImageGridProps {
  entity: ImmutableEntity<Type>;
  // 'slider' = carousel everywhere (timeline); 'default' = grid + mobile slider.
  displayMode?: DisplayMode;
  headingTag?: HeadingTag;
  textColClass?: string;
  mediaColClass?: string;
}

const RATIO: Record<'square' | 'landscape' | 'portrait', AspectRatio> = {
  square: '1_1',
  landscape: '4_3',
  portrait: '3_4',
};

const IMAGE_STYLE_SIZES = ['xs', 'sm', 'md', 'xl'] as const;

// SCREEN_NORMAL (768px) is the SCSS $tablet breakpoint where the 2-up starts;
// below it the slider keeps its peeking variable width.
const SLIDER_RESPONSIVE = [
  { breakpoint: SCREEN_NORMAL, settings: { slidesToShow: 1, variableWidth: true } },
];

const ParagraphTextImageGrid: React.FC<ParagraphTextImageGridProps> = ({
  entity,
  displayMode = 'default',
  headingTag,
  textColClass,
  mediaColClass,
}) => {
  const paragraph = entity.toJS();

  const title = paragraph.field_title?.[0]?.value;
  const body = paragraph.field_body?.[0]?.processed ?? '';
  const media = paragraph.field_media_items ?? [];
  const isSlider = displayMode === 'slider';

  // The grid composes 2 or 4 tiles; the slider shows every image.
  const gridMedia = media.filter((_, i) => !((media.length === 3 && i === 2) || i > 3));
  const items = isSlider ? media : gridMedia;

  const resolveRatio = (index: number): AspectRatio => {
    if (isSlider) {
      return RATIO.landscape;
    }
    if (items.length < 4) {
      return RATIO.square;
    }
    // 1st and 4th tile are landscape, the middle two are portrait.
    return index % 3 === 0 ? RATIO.landscape : RATIO.portrait;
  };

  const buildItems = (lazyload: boolean) => items.map((item, index) => {
    const caption = item.entity?.field_caption?.[0]?.value;
    const ratio = resolveRatio(index);
    const src = IMAGE_STYLE_SIZES.map((size) => item.styles?.[`ratio_${ratio}_${size}`] ?? '');

    return (
      <Image
        key={item.target_id ?? index}
        src={src}
        alt={caption}
        lazyload={lazyload}
      />
    );
  });

  return (
    <div
      className={classNames('Paragraph ParagraphTextImageGrid', {
        'ParagraphTextImageGrid--slider': isSlider,
      })}
    >
      <Container>
        <div className="row">
          <div className={textColClass || 'col-12 col-md-10 offset-md-1 col-lg-8 offset-lg-2'}>
            <Text
              title={title}
              text={body}
              className="text--regular"
              headingTag={headingTag}
            />
          </div>

          {isSlider ? (
            <div className={mediaColClass || 'col-12 offset-md-1 col-md-11 offset-lg-2 col-lg-10'}>
              <div className="ParagraphTextImageGrid__slider">
                <Slider slidesToShow={2} variableWidth={false} responsive={SLIDER_RESPONSIVE}>
                  {buildItems(false)}
                </Slider>
              </div>
            </div>
          ) : (
            <div className="col-12">
              {gridMedia.length === 4 && (
                <div className="ParagraphTextImageGrid__slider">
                  <Slider>
                    {buildItems(false)}
                  </Slider>
                </div>
              )}
              <div className="ParagraphTextImageGrid__grid">
                <Container>
                  <MasonryGrid items={buildItems(true)} />
                </Container>
              </div>
            </div>
          )}
        </div>
      </Container>
    </div>
  );
};

export default ParagraphTextImageGrid;
